import React, { useState, useEffect } from 'react';
import styles from './CountdownTimer.module.css';

interface Event {
  Event_ID: string;
  title: string;
  event_date: string;
  location_name?: string;
  street?: string;
  house_number?: string;
  postal_code?: string;
  city?: string;
  country?: string;
}

interface CountdownTimerProps {
  event: Event;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({ event }) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const eventDate = new Date(event.event_date);
      const now = new Date();
      const difference = eventDate.getTime() - now.getTime();

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setTimeLeft({ days, hours, minutes, seconds });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [event.event_date]);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleShowLocation = () => {
    if (event.location_name && event.street && event.city && event.country) {
      const locationString = `${event.location_name}, ${event.street} ${event.house_number || ''}, ${event.postal_code || ''} ${event.city}, ${event.country}`;
      
      // Try to open in Google Maps
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationString)}`;
      window.open(googleMapsUrl, '_blank');
    }
  };

  return (
    <div className={styles.viewContainer}>
      <div className={styles.countdownContainer}>
        <div className={styles.eventInfo}>
          <h1 className={styles.eventTitle}>{event.title}</h1>
          <p className={styles.eventDate}>
            {formatDate(event.event_date)} at {formatTime(event.event_date)} (BST)
          </p>
          
          {/* Location Button */}
          {event.location_name && event.street && event.city && event.country && (
            <div className={styles.locationContainer}>
              <button 
                className={styles.locationBtn}
                onClick={handleShowLocation}
                title="Show location"
              >
                <i className="bi bi-pin-map"></i> View Location
              </button>
            </div>
          )}
        </div>

        <div className={styles.countdownTimer}>
          <div className={styles.countdownBox}>
            <div className={styles.countdownValue}>{timeLeft.days}</div>
            <div className={styles.countdownLabel}>Days</div>
          </div>

          <div className={styles.countdownBox}>
            <div className={styles.countdownValue}>{timeLeft.hours}</div>
            <div className={styles.countdownLabel}>Hours</div>
          </div>

          <div className={styles.countdownBox}>
            <div className={styles.countdownValue}>{timeLeft.minutes}</div>
            <div className={styles.countdownLabel}>Minutes</div>
          </div>

          <div className={styles.countdownBox}>
            <div className={styles.countdownValue}>{timeLeft.seconds}</div>
            <div className={styles.countdownLabel}>Seconds</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CountdownTimer;
